import { useState, useCallback } from "react";

import { useLazyQuery } from "@apollo/client";

import { OperatorType, QueryParams } from "@regal-voice/ui";
import { VALID_PHONE_CHARACTERS_REGEX, INVALID_EMAIL_REGEX, INVALID_NAME_REGEX } from "Services/CommunicationService";
import { Profile } from "Types/Profile";

import { MIN_REQUIRED_CHARS } from "../SearchableDialPad/ContactSearch/ContactSearch";
import { getPhonesEmails } from "../SearchableDialPad/ContactSearch/queries";
import { GetProfilesQueryReturn } from "../SearchableDialPad/SearchableDialPad.types";

const defaultVariables: Partial<QueryParams> = {
    orderBy: [
        {
            key: "updatedAt",
            value: "DESC",
        },
    ],
    page: 0,
    pageSize: 25,
};

export function useProfileLookupSearch() {
    const [profiles, setProfiles] = useState<Profile[]>([]);

    const [loadProfiles, { loading }] = useLazyQuery<GetProfilesQueryReturn>(getPhonesEmails, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setProfiles(data?.getPhonesEmails?.items || []);
        },
    });

    const handleSearchByPhoneNameEmailOrExternalId = useCallback(
        (value: string) => {
            if (!value) {
                setProfiles([]);
                return;
            }

            if (value.length < MIN_REQUIRED_CHARS) {
                setProfiles([]);
                return;
            }

            // Determine search type based on input
            const searchBy = VALID_PHONE_CHARACTERS_REGEX.test(value)
                ? "contactPhone"
                : INVALID_NAME_REGEX.test(value)
                  ? "email"
                  : "contactName";

            const filterBy = [
                {
                    key: searchBy === "contactName" ? "name" : searchBy,
                    value,
                    operator: OperatorType.STARTS_WITH,
                },
            ];

            // If searching by name and value could be an email, also search email
            if (searchBy === "contactName" && !INVALID_EMAIL_REGEX.test(value)) {
                filterBy.push({
                    key: "email",
                    value,
                    operator: OperatorType.STARTS_WITH,
                });
            }

            // Also search by external ID if it looks like it could be one
            if (searchBy === "contactName") {
                filterBy.push({
                    key: "externalId",
                    value,
                    operator: OperatorType.STARTS_WITH,
                });
            }

            loadProfiles({
                variables: {
                    queryParams: {
                        ...defaultVariables,
                        filterBy,
                    },
                },
            });
        },
        [loadProfiles]
    );

    return {
        handleSearchByPhoneNameEmailOrExternalId,
        loading,
        profiles,
    };
}
